/*
 * a11y-onetrust.js — accessibility repairs for the OneTrust cookie banner.
 * AudioEye Aug 2026: HOME-07 (2.4.3 Focus Order), HOME-16 (2.5.3 Label in Name).
 *
 * The banner is injected by the OneTrust SDK, but the SDK is loaded from our own
 * layout and calls window.OptanonWrapper once the banner is on the page, so this
 * is ours to correct — no console change or vendor ticket needed.
 *
 * Both repairs were measured on the live banner before being written here: with
 * them applied every control keeps exactly the same x/y/width/height, so nothing
 * moves on screen.
 */
(function () {
  'use strict';

  var BANNER = '#onetrust-banner-sdk';

  /* HOME-07 — tab order did not follow the banner's visual order. Measured live:
     Cookie Policy (y521) -> Accept (x556) -> Cookie Settings (x365) -> Close (y440),
     against a reading order of Close, Cookie Policy, Cookie Settings, Accept.

     Two separate causes, each fixed by moving a node rather than by adding
     tabindex, which would drag the banner ahead of the rest of the page:

     1. Accept sits in .onetrust-banner-options, which is float:right, while
        Cookie Settings is not floated. The float pulls Accept to the right edge
        even though it comes first in the DOM, so the two read left-to-right in
        the opposite order to the DOM. Putting Settings first restores agreement;
        the float still parks Accept on the right, so the layout is unchanged.
     2. The close button is visually at the top of the banner but last in the DOM.
        Its container is position:absolute, so moving it first costs nothing
        visually. */
  function fixOrder(banner) {
    var group = banner.querySelector('#onetrust-button-group');
    if (group) {
      var options = group.querySelector('.onetrust-banner-options');
      var settings = group.querySelector('#onetrust-pc-btn-handler');
      if (options && settings && options.compareDocumentPosition(settings) &
          Node.DOCUMENT_POSITION_FOLLOWING) {
        group.insertBefore(settings, options);
      }
    }

    var close = banner.querySelector('#onetrust-close-btn-container');
    if (close && close.parentElement && close.parentElement.firstChild !== close) {
      close.parentElement.insertBefore(close, close.parentElement.firstChild);
    }
  }

  /* HOME-16 — the Cookie Policy link carried
     aria-label="More information about your privacy, opens in a new tab", which
     replaces the visible "Cookie Policy" entirely. Speech users asking for
     "Cookie Policy" then have nothing to match, and the name no longer contains
     the visible text. The link really is target="_blank", so the new-tab warning
     is kept — it just has to follow the visible words rather than replace them. */
  function fixPolicyLink(banner) {
    var link = banner.querySelector('.ot-cookie-policy-link');
    if (!link) return;
    var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    var label = link.getAttribute('aria-label') || '';
    if (label.toLowerCase().indexOf(text.toLowerCase()) === 0) return;
    var newTab = link.getAttribute('target') === '_blank';
    link.setAttribute('aria-label', newTab ? text + ', opens in a new tab' : text);
  }

  function sweep() {
    var banner = document.querySelector(BANNER);
    if (!banner) return;
    fixOrder(banner);
    fixPolicyLink(banner);
  }

  function start() {
    sweep();
    if (!('MutationObserver' in window)) return;
    /* The banner is injected late and is rebuilt when it is reopened from
       "Cookie Settings" or after a consent change, so keep watching. */
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; sweep(); });
    }).observe(document.documentElement, { subtree: true, childList: true });
  }

  /* The SDK calls this once the banner is rendered; chain rather than replace, in
     case anything else has already claimed it. */
  var previous = window.OptanonWrapper;
  window.OptanonWrapper = function () {
    if (typeof previous === 'function') {
      try { previous.apply(this, arguments); } catch (e) {}
    }
    sweep();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
